import { useAtom } from 'jotai'
import { useMemo } from 'react'
import {
  contextSourcesAtom,
  useCurrentWindowToSelection,
  useIsCurrentWindowAttached,
  useSelectedWindows,
  useUnselectedWindows,
  useWindowSelection,
} from '../store/atoms'
import { AttachmentMenuItemType } from '../components/chat/input/context/menu/attachments-menu'

export const useUniqueWindowSources = () => {
  const [sources, setSources] = useAtom(contextSourcesAtom)
  const selectedWindows = useSelectedWindows()
  const unselectedWindows = useUnselectedWindows()
  const { isWindowSelected, isWindowUnselected } = useWindowSelection()
  const isCurrentWindowAttached = useIsCurrentWindowAttached()
  const currentWindow = useCurrentWindowToSelection()

  const windows = useMemo(() => {
    // Create a Map to track unique windows by PID (or other unique identifier)
    const uniqueWindowsMap = new Map()

    // Process windows and only keep unique ones
    sources.windows.forEach((window, index) => {
      // Create a composite key from multiple properties to ensure uniqueness
      const windowTitle = window.windowTitle ?? ''
      const appName = window.appName ?? ''
      const pid = window.pid ?? -1

      // Create a uniqueness key - you can adjust this based on what properties reliably identify a window
      const uniquenessKey = `${pid}-${windowTitle}`

      // Only add this window to our map if we haven't seen this unique key before
      if (!uniqueWindowsMap.has(uniquenessKey)) {
        const isCurrentWindow =
          window.pid === currentWindow?.pid &&
          currentWindow &&
          (windowTitle.includes(currentWindow?.windowTitle) || currentWindow?.windowTitle?.includes(windowTitle))

        const isExplicitlySelected = isWindowSelected({
          pid,
          windowTitle,
          appName,
        })
        const isExplicitlyUnselected = isWindowUnselected({
          pid,
          windowTitle,
          appName,
        })

        uniqueWindowsMap.set(uniquenessKey, {
          window,
          id: uniquenessKey, // Use the uniqueness key as the ID
          label: window.windowTitle ?? window.appName ?? 'Window',
          icon: window.appIcon ? <img src={window.appIcon} className={'h-5 w-5'} alt="app icon" /> : undefined,
          selected: !isExplicitlyUnselected && (isExplicitlySelected || (isCurrentWindow && isCurrentWindowAttached)),
          selectable: true,
        })
      }
    })

    // Convert map values back to an array
    return Array.from(uniqueWindowsMap.values()) as AttachmentMenuItemType[]
  }, [
    sources,
    currentWindow,
    isCurrentWindowAttached,
    isWindowSelected,
    isWindowUnselected,
    selectedWindows,
    unselectedWindows,
  ])

  return windows
}
